import type { Locale } from './index'

/**
 * Backend error messages in the reader's language. The Go API answers with
 * English fmt text ("warehouse %s is inactive", filled in). `server/<locale>.json`
 * holds every format the backend can send — collected from its source by
 * `scripts/server-messages.mjs` — with a translation whose {1}, {2}… stand for
 * the format's verbs in order, labels for the field names that `%s` verbs
 * carry ("%s is required" with client_id → «Клиент»: обязательное поле.), and
 * the enum words some messages carry ("driver is %s" with repairing → на ремонте).
 *
 * The catalog loads in the background with the dictionary; until it arrives,
 * or for a message it does not know, the server text is shown as it is.
 */

type Catalog = { messages: Record<string, string>; fields: Record<string, string>; values: Record<string, string> }
type Matcher = { format: string; pattern: RegExp; verbs: string[] }

const catalogs: Partial<Record<Locale, Catalog>> = {}
const pending: Partial<Record<Locale, Promise<void>>> = {}
const loaders: Record<Locale, () => Promise<Catalog>> = {
  en: () => import('./server/en.json').then((module) => module.default),
  ru: () => import('./server/ru.json').then((module) => module.default),
  uz: () => import('./server/uz.json').then((module) => module.default),
  // Ўзбекча comes from O'zbekcha, as the dictionaries do; {n} placeholders survive transliteration.
  cyr: () => Promise.all([import('./server/uz.json'), import('./transliterate')]).then(([uz, { transliterateTree }]) => transliterateTree(uz.default)),
}

/** Starts loading a locale's catalog; resolves when it is in memory. Never rejects. */
export function loadServerMessages(locale: Locale): Promise<void> {
  pending[locale] ??= loaders[locale]()
    .then((catalog) => {
      catalogs[locale] = catalog
    })
    .catch(() => {
      delete pending[locale]
    })
  return pending[locale]
}

const VERB = /%[-+# 0]*\d*(?:\.\d+)?([a-zA-Z%])/g
const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

function compile(format: string): Matcher {
  let source = '^'
  let last = 0
  const verbs: string[] = []
  for (const match of format.matchAll(VERB)) {
    source += escape(format.slice(last, match.index))
    last = match.index + match[0].length
    const verb = match[1]
    if (verb === '%') {
      source += '%'
      continue
    }
    verbs.push(verb)
    source += verb === 'd' ? '(-?\\d+)' : verb === 'q' ? '"((?:[^"\\\\]|\\\\.)*)"' : '([\\s\\S]*?)'
  }
  // A format ending in a space is the head of a message the backend concatenates ("… is limited to " + size).
  const tail = format.endsWith(' ') ? '' : '$'
  return { format, pattern: new RegExp(`${source}${escape(format.slice(last))}${tail}`), verbs }
}

/**
 * Formats with verbs, most specific first: the more fixed text a format has,
 * the earlier it is tried, so "%s" (anything) is the last resort. The formats
 * are the same in every locale, so this is built once.
 */
let matchers: Matcher[] | null = null
function matchersFor(catalog: Catalog) {
  matchers ??= Object.keys(catalog.messages)
    .filter((format) => /%[^%]/.test(format.replace(/%%/g, '')) || format.endsWith(' '))
    .map(compile)
    .sort((a, b) => b.format.replace(VERB, '').length - a.format.replace(VERB, '').length)
  return matchers
}

function unquote(value: string) {
  try {
    return JSON.parse(`"${value}"`) as string
  } catch {
    return value
  }
}

/** A field name as its label: client_id, items[2].client_id and client id all read «Клиент». */
function field(catalog: Catalog, value: string) {
  const name = value.replace(/\[\d+\]/g, '').split('.').pop()!.trim().replace(/ /g, '_')
  return catalog.fields[value] ?? catalog.fields[name]
}

function translate(catalog: Catalog, message: string, depth: number): string | undefined {
  const exact = catalog.messages[message]
  if (exact !== undefined) return exact
  if (depth > 2) return undefined
  for (const matcher of matchersFor(catalog)) {
    const match = matcher.pattern.exec(message)
    if (!match) continue
    const template = catalog.messages[matcher.format]
    // A %s or %v is a nested message, a field name or an enum word; numbers and %q values are data.
    const values = matcher.verbs.map((verb, index) => {
      const value = match[index + 1]
      if (verb === 'q') return unquote(value)
      if (verb !== 's' && verb !== 'v') return value
      return translate(catalog, value, depth + 1) ?? field(catalog, value) ?? catalog.values[value] ?? value
    })
    return template.replace(/\{(\d+)\}/g, (placeholder, n: string) => values[Number(n) - 1] ?? placeholder)
  }
  return undefined
}

/** The server's message in `locale`, or undefined when the catalog is not loaded or does not know it. */
export function translateServerMessage(message: string, locale: Locale): string | undefined {
  const catalog = catalogs[locale]
  if (!catalog || !message) return undefined
  const text = translate(catalog, message.trim(), 0)
  // "%s" alone matches anything: only trust it when the nested value was itself translated.
  return text === message.trim() ? undefined : text
}
